import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../i18n';
import { ExtractionRequest, AgentPreset, AgentPresetsResponse } from '../types';
import { Button } from './animate-ui/components/buttons/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { HiOutlineSparkles } from "react-icons/hi2";
import { MdError } from "react-icons/md";
import { AiOutlineOpenAI } from "react-icons/ai";
import { SiAnthropic, SiGoogle } from "react-icons/si";
import { BsTwitterX } from "react-icons/bs";
import axios from 'axios';
import { OpenRouterIcon } from './icons/OpenRouterIcon';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface ExtractionFormProps {
  onSubmit: (request: ExtractionRequest) => void;
  isConnected: boolean;
  isExtracting: boolean;
  onCancel?: () => void; // optional cancellation callback supplied by parent
}

const QUEUE_STORAGE_KEY = 'extraction_queue_v1';
const LAST_CANCELED_KEY = 'extraction_last_canceled_v1';
const MAX_QUEUE_LENGTH = 10;

// Preset display metadata (frontend-only)
const PRESET_DISPLAY_INFO: Record<string, {
  nameKey: string;
  descriptionKey: string;
  icon: string;
}> = {
  fast: {
    nameKey: 'form.preset_fast_name',
    descriptionKey: 'form.preset_fast_description',
    icon: '⚡',
  },
  balanced: {
    nameKey: 'form.preset_balanced_name',
    descriptionKey: 'form.preset_balanced_description',
    icon: '⚖️',
  },
  extended: {
    nameKey: 'form.preset_extended_name',
    descriptionKey: 'form.preset_extended_description',
    icon: '🧠',
  },
};

const applyModeDefaults = (request: ExtractionRequest): ExtractionRequest => ({
  ...request,
  run_diagnostic: request.run_diagnostic !== false,
  run_pcs: Boolean(request.run_pcs),
  pcs_include_refinement:
    request.pcs_include_refinement === undefined
      ? true
      : request.pcs_include_refinement,
});

export const ExtractionForm: React.FC<ExtractionFormProps> = ({
  onSubmit,
  isConnected,
  isExtracting,
  onCancel
}) => {
  const [formData, setFormData] = useState<ExtractionRequest>(() =>
    applyModeDefaults({
      text: '',
      agent_preset: 'balanced',
      max_queries: 6,
      k_per_query: 4,
      max_codes: 5,
      use_rag: true,
      temperature: 0.1,
      feedback: undefined,
      run_diagnostic: true,
      run_pcs: false,
      pcs_include_refinement: true,
    })
  );
  // Queue of pending extractions (each item is a full ExtractionRequest)
  const [pendingQueue, setPendingQueue] = useState<ExtractionRequest[]>([]);
  const [currentRequest, setCurrentRequest] = useState<ExtractionRequest | null>(null);
  const [lastCanceledRequest, setLastCanceledRequest] = useState<ExtractionRequest | null>(null);
  const [queueLimitAlert, setQueueLimitAlert] = useState(false);
  // Track last submission status to show transient feedback
  const [justQueued, setJustQueued] = useState(false);
  const justQueuedTimerRef = useRef<number | null>(null);
  const prevIsExtracting = useRef<boolean>(isExtracting);
  const [modeError, setModeError] = useState<string | null>(null);
  const [agentPresets, setAgentPresets] = useState<Record<string, AgentPreset>>({});
  const [presetsLoading, setPresetsLoading] = useState(true);
  const { t, locale } = useTranslation();
  
  // Fetch available agent presets on mount
  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const response = await axios.get<AgentPresetsResponse>(`${API_BASE_URL}/agent/presets`);
        setAgentPresets(response.data.presets);
      } catch (error) {
        console.error('Failed to fetch agent presets:', error);
        // Set default presets as fallback
        setAgentPresets({
          balanced: {
            key: 'balanced',
            available: true,
          },
        });
      } finally {
        setPresetsLoading(false);
      }
    };
    fetchPresets();
  }, []);
  
  // When ongoing extraction finishes, automatically submit the next queued request.
  useEffect(() => {
    // Transition from extracting -> not extracting
    if (prevIsExtracting.current && !isExtracting) {
      setPendingQueue(q => {
        if (q.length === 0) return q;
        const [next, ...rest] = q;
        // Fire next extraction
        const normalized = applyModeDefaults(next);
        setCurrentRequest(normalized);
        onSubmit(normalized);
        return rest;
      });
    }
    prevIsExtracting.current = isExtracting;
  }, [isExtracting, onSubmit]);

  // Cleanup queued feedback timer
  useEffect(() => {
    return () => {
      if (justQueuedTimerRef.current) {
        window.clearTimeout(justQueuedTimerRef.current);
      }
    };
  }, []);

  // Load persisted queue and last canceled on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(QUEUE_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setPendingQueue(parsed.map((item: ExtractionRequest) => applyModeDefaults(item)));
        }
      }
      const canceledRaw = localStorage.getItem(LAST_CANCELED_KEY);
      if (canceledRaw) {
        const parsedCanceled = JSON.parse(canceledRaw);
        if (parsedCanceled && typeof parsedCanceled === 'object') {
          setLastCanceledRequest(applyModeDefaults(parsedCanceled));
        }
      }
    } catch (e) {
      console.warn('Failed loading persisted queue', e);
    }
  }, []);

  // Persist queue changes
  useEffect(() => {
    try { localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(pendingQueue)); } catch (e) {
      console.warn('Failed persisting queue', e);
    }
  }, [pendingQueue]);

  const removeQueued = (index: number) => setPendingQueue(q => q.filter((_, i) => i !== index));
  const moveQueued = (index: number, dir: -1 | 1) => setPendingQueue(q => {
    const target = index + dir; if (target < 0 || target >= q.length) return q; const copy = [...q]; const [item] = copy.splice(index,1); copy.splice(target,0,item); return copy;
  });
  const editQueued = (index: number) => {
    setFormData(applyModeDefaults(pendingQueue[index]));
    removeQueued(index);
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    if (currentRequest) {
      const normalized = applyModeDefaults(currentRequest);
      setLastCanceledRequest(normalized);
      try { localStorage.setItem(LAST_CANCELED_KEY, JSON.stringify(normalized)); } catch {}
    }
  };

  const handleRerunCanceled = () => {
    if (lastCanceledRequest) {
      const normalized = applyModeDefaults(lastCanceledRequest);
      setCurrentRequest(normalized);
      onSubmit(normalized);
      setLastCanceledRequest(null);
      try { localStorage.removeItem(LAST_CANCELED_KEY); } catch {}
    }
  };

  const handleModeToggle = (key: 'run_diagnostic' | 'run_pcs') => (checked: boolean) => {
    setFormData(prev => {
      const next = applyModeDefaults({ ...prev, [key]: checked });
      if (next.run_diagnostic || next.run_pcs) {
        setModeError(null);
      } else {
        setModeError(t('form.mode_required', 'Select at least one extraction mode.'));
      }
      return next;
    });
  };

  const hasSelectedMode = formData.run_diagnostic !== false || !!formData.run_pcs;

  const [sampleTexts] = useState([
    {
      nameKey: 'simple',
      text: 'Varón de 65 años con urgencia urinaria y polaquiuria nocturna. Es diagnosticado de HBP. Se realiza prostatectomía radical abierta con resección simultánea de vesículas seminales.'
    },
    {
      nameKey: 'medio',
      text: "Motivo de Consulta ISQUEMIA DE MID Antecedentes Familiares: No antecedentes anestésicos familiares Personales: -No RAMCs - No hábitos tóxicos - HTA. - DM tipo 2 con nefropatía y retinopatía diabéticas. No DLP - Cardiopatia isquemica. IAM no Q con revascularizacion percutanea (2008). - Miocardiopatía dilatada. Insuficiencia mitral leve. - Retraso mental moderado. Esquizofrenia. Institucionalizado IQ: Hernia umbilical. Neoplasia de colon derecho - Institucionalizado. Dependiente parcial para las ABVD. - Tto habitual: * Actrapid innolet 2-2-0 * Duphalac 1-0-1 * Lansoprazol 30mg 1-0-0 * Lormetazepam 1mg 0-0-1'5 * Quetiapina 300mg 0'5-0-1'5 * Ramipril 2'5mg 1-0-1 * Simvastatina 20mg 0-0-1 * Torasemida 5mg 1-0-0 * Toujeo 48-0-36 * Tromalyt 150mg 0-0-1 * Xepilon 50mg 1-0-0 Enfermedad Actual Anamnesis: Remitido a nuestra consulta por ulcera de pie con mala evolución Se deriva al servicio de Urgencias Exploración: Ulceracion profunda de cara externa de pie derecho con exposicion tendinosa y osea, mal olor y supuración evidente. Cianosis en calcetin. Estuporoso. MEG Evolución y curso Clínico Situacion de inestabilidad hemodinámica, con taquicardia sudoracion e hipotensión. Oligoanuria. Se decide maniobras de reanimacion , correción de la coagulación e intervención urgente Favorable en el postoperatorio, salvo descompensacion de sus glucemias Intervencion Quirurgica / Procedimientos -Fecha de Intervención URGENTE: 22/01/2018 21:20: Amputacion supracondílea Juicio Clínico Principal: ISQUEMIA EN MID NECROSIS GRADO 5 DE RUTHERFORD Y GANGRENA Secundarios: a.: SEPSIS DESCOMPENSACION DIABETICA Tratamiento QUIRURGICO: AMPUTACION SUPRACONDILEA Plan de Actuación Curas periódicas por enfermeria de ZBS retirando sutura a partir del 1/2/19 Paracetamol 1g c/8 h Clindamicina 300 mg c/ 8 h ( tres dias más) medicacion domiciliaria previa Insulina Toujeo 40-0-15 y ajustar con pauta de correccion segun su médico",
    },
    {
      nameKey: 'complejo',
      text: `ANTECEDENTES PERSONALES:Trastorno depresivo

ANAMNESIS:Mujer de 36 años que acude al hospital Santos Reyes de Aranda de Duero trasladado por Emergencias tras precipitación desde 2º
piso. A la llegada de servicio de Emergencias a la zona paciente consciente y reactiva a estímulos con scalp en cuero cabelludo.
Posteriormente disminución de nivel de consciencia por lo que se decide intubación;  tras varios intentos infructuosos se decide
colocar mascarilla laríngea y trasladar a nuestro centro.

TC Craneal: Estructuras de la línea media centradas. Cisternas basales y peritronculares amplias ylibres. Sistema ventricular de
tamaño y morfología normales. Ocupación de variossurcos de la convexidad izquierda por contenido espontáneamente
hiperdenso y de lacisura de Silvio izquierdo, que asocia engrosamiento del espacio extraaxial de lamisma densidad de predominio
en región temporal ipsilateral en relación conhemorragia subaracnoidea y colección extraaxial (probable hematoma
subduralasociado), observando un área córtico-subcortical hipodensa en lóbulo temporalizquierdo de probable origen contusivo,
que recomendamos valorar en próximo controlradiológico para ver evolución. Edema de surcos corticales a nivel de la
convexidadizquierda. Engrosamiento laminar espontáneamente hiperdenso de la vertienteizquierda del tentorio del cerebro que
sugiere componente de hematoma subdural adicho nivel. Línea de fractura en región temporal derecha que alcanza el techo y
lapared lateral de la órbita derecha con presencia de burbujas aéreas intraorbitarias yaumento de planos blandos en el techo
orbitario, que posteriormente se extiende haciael peñasco (fractura longitudinal) con ocupación parcial de celdillas mastoideas y
oídomedio (afectación de cadena de huesecillos) y extensión a la pared anterior del CAE homolateral. Neumoencéfalo en
hemisferio cerebeloso derecho. No podemosdescartar la presencia de alguna línea de fractura a nivel del peñasco izquierdo ya
que se objetivan aisladas burbujas de neumoencéfalo en fosa temporal anterior y existetambién ocupación de celdillas mastoideas
y oído medio. Línea de fractura de huesospropios, en pared medial de la órbita derecha, a nivel de la lámina papirácea y
probablemente del tabique nasal que condiciona ocupación de celdillas etmoidales.Fractura de apófisis pterigoidea izquierda y de
la pared lateral de seno esfenoidal ipsilateral (ocupación de senos esfenoidales) que parece alcanzar el vértice de laórbita
izquierda, con presencia de burbujas aéreas intraorbitarias; línea de fractura delproceso clinoideo anterior izquierdo. Dudosa línea
de fractura de la pared medial de laórbita izquierda. Fractura del suelo-pared anterior del seno maxilar izquierdo con
engrosamiento mucoperióstico. Hematoma subgaleal en región frontoparietotemporal derechaTAC Abdominal: No signos radiológicos de neumoperitoneo. Se aprecia una tenue y mal delimitadaárea hipocaptante en
segmento VII hepático de significado incierto (podríacorresponder parcialmente con artefacto) no obstante en el contexto de la
paciente nopodemos descartar pequeña área de contusión/hipoperfusión por lo que serecomienda valorar en el próximo control
radiológico en función de los datos clínicos yanalíticos. Lengüeta de líquido perihepático. Importante distensión gástrica.
Importantedistensión vesical. Se recomienda sondaje vesical. Realce homogéneo del restovísceras sólidas sin que se aprecien
otras alteraciones.TC Torácico: Estudio escasamente inspirado y parcialmente artefactado pormovimiento. Extensos aumentos de densidad mal
delimitados en la regiónposterior de ambos hemitórax, depredominio derecho a descartarcontusión/broncoaspiración. Silueta
cardíaca y restode estructurasmediastínicas dentro de la normalidad Pequeño neumotórax enregiónapical, anterior y basal
derecho. Derrame pleural derecho. Pequeñoenfisema del tejidocelular subcutáneo en pared torácica derecha. No seaprecia
derrame pericárdico.Aumento densidad en mediastino superior quedada la edad de la paciente pudieracorresponder con restos
tímicos, noobstante se recomienda valorar en próximo controlTC Columna cervical:No se objetivan líneas de fractura. Conservación de la altura y alineación de loscuerpos vertebrales sin
objetivar alteraciones del muro posterior.TC Esqueleto:Fractura conminuta y desplazada del tercio medio-distal de la clavícula derecha(incluida parcialmente en la
exploración). Solución de continuidad de la cortical anteriordel segundo arco costal derecho sugestivo de fisura. Línea de fractura
en tercioposterior de arcos costales derechos: tercero, cuarto (acabalgado), quinto, sexto,séptimo, octavo y noveno (acabalgado y
desplazado) y décimo (doble) y duodécimo(costilla rudimentaria). Línea de fractura en tercio anterior de arco costales
derechos:cuarto (dudosos), quinto (acabalgado), séptimo y octavo (desplazado) y noveno. Líneade fractura de apófisis transversa
derecha de T7 y T8 y de L1 y L2 (estas dos últimasdesplazadas). Fractura del cuerpo vertebral de L3 que alcanza el platillo
superior y cara anterior, sin condicionar alteración del muro posterior.

07/06/2025 (H. Burgos)
TC CRÁNEO: No muestra cambios significativos el componente hemorrágico intracraneal extraaxial consistente en:- Láminas
de hemorragia subaracnoidea hemisféricas izquierdas que predominan a nivel del polo temporal izquierdo.- Colección hemática
extraaxial con morfología semilunar fronto-parieto-temporal izquierda, que alcanza un grosor máximo a nivel temporal de unos 7
mm.También permanece estable la hipodensidad temporal izquierda, de probable naturaleza contusiva.En conjunto ejercen efecto sobre los surcos hemisféricos izquierdos, estando la línea media centrada y las cisternas de la base
permeables.Sensor PIC con extremo distal en la sustancia blanca frontal izquierda.Fracturas cráneo-faciales conocidas que
afectan en el lado derecho a la escama temporal, al peñasco (fractura longitudinal), paredes superior y lateral de la órbita; en el
lado a las tres paredes del seno maxilar y a la apófisis pterigoides (LeFort tipo I izquierdo); en línea media a la base de cráneo a
nivel de etmoides y esfenoides, y a los huesos propios de la nariz. Hemoseno esfenoidal, etmoidal y maxilar izquierdo.
Engrosamiento mucoso del seno maxilar derecho. Resto de estructuras faciales no incluidas en este estudio.Hematoma epicraneal
parietotemporal derecho.ANGIOTC TSA: No se ven defectos de repleción ni otras alteraciones significativas en sistemas carotídeo, vertebrobasilar ni en
ramas del polígono de Willis.Vascularización córtico-subcortical conservada y simétrica.Senos venosos cerebrales
permeables.Rectificación de la lordosis cervical fisiológica, sin identificarse líneas de fractura.TC TÓRAX: Neumotórax laminar bilateral, el derecho con cámara máxima apical de 5 mm y el izquierdo con cámara máxima
anterior de 3 mm. Neumomediastino anterior que se extiende rodeando a la musculatura y los vasos a nivel cervical anterior y
hacia la región axilar derecha.Consolidaciones pulmonares parcheadas en ambos lóbulos superiores, lóbulo medio y língula,
compatibles con contusiones pulmonares, más extensas en LSI/ língula.Consolidaciones/ atelectasias de ambos lóbulos
inferiores.Dos tubos de tórax, el que tiene entrada por el 2 espacio intercostal anterior con extremo distal a nivel póstero-basal y
el que entra por el 6 espacio intercostal posterior tiene su extremo distal a nivel póstero-superior.Tubo de intubación con extremo
distal en carina introduciéndose discretamente hacia el bronquio principal derecho, valorar retirar 1-2 cm.Vía venosa central con
extremo distal en aurícula derecha.Sonda nasogástrica, no estando incluido en el estudio su extremo distal (más allá de cuerpo
gástrico).Fracturas costales derechas (al menos del primero al décimo arcos costales, ya que el undécimo y doceavo no están
incluidos en el estudio, algunas de ellas dobles), izquierda (primer arco costal anterior) y de la clavícula derecha.Parénquima
hepático con zonas parcheadas hipodensas que pudieran estar en relación con contusiones, a correlacionar con TC de abdomen
realizado en otro centro.20/06/2025 TC CEREBRO:  Evolución radiológica desfavorable con mayor efecto de masa, desplazamiento de línea media y
signos de herniación subfalcina y uncal izquierda, sin evidencia de oclusión arterial intracraneal.21/06/2025 TC CEREBRO: Cambios postquirúrgicos secundarios a craniectomía descompresiva frontotemporoparietal
izquierda: Herniación cerebral a través de la craniectomía. Persiste hipodensidad  temporoparietal izquierda, observando en su
espesor un pequeña colección hemorrágica. Leve colección hemorrágica extraaxial frontal izquierda con material de
hemostasia. En región frontal (en área malácica ya conocida) se observa otra colección hemorrágica en probable relación con
manipulación del sensor PIC (retirado). Persiste importante desviación de línea media (casi similar al previo). Sensor subcutáneo
en región temporal izquierdo

EVOLUCIÓN:Ingreso inicial en UCI donde se realiza ecografía pulmonar que muestra s ausencia de sliding en hemitórax derecho  por lo que se
colocan dos tubos de tórax (lateral y anterior derechos) con mejoría progresiva de la TA y satO2. Desde el punto de vista
neurológico, se inician medidas antiedema, y se coloca sensori de PIC por Neurocirugía con. PICS controladas inicialmente sin
necesidad de osmoterapia. Fracaso multiorgánico inicial (coagulación, renal, metabólico, plaquetas,hemodinámico, respiratorio)
por lo que se decide colocación de shaldom femoral derecho e inicio de terapia de reemplazo renal continuo. Se amplia cobertura
antibiótica. Progresivamente buena evolución, sobretodo desde el punto de vista respiratorio que permite retirada de tubo tórax
lateral derecho. Sin embargo, a los pocos días se constata empeoramiento neurológico con incremento de PICs y empeoramiento
radiológico por lo que se decide tratamiento quirúrgico mediante craniectomía descompresiva. Dada la necesidad de nutrición y
soporte ventilatorio prolongado se decide traqueostomía y colocación de gastrostomía.  Posteriormente buena evolución clínica
que permite retirada de la sedación y cierre progresivo de traqueostomía. Se inicia tratamiento rehabilitador con mejoría
progresiva de hemiparesia derecha y logopeda que permite inicio de deglución. Valorada por Traumatología tras la reducción de
la fractura distal de radio quien retira yeso con buena evolución. Dada la buena evolución clínica se decide el 1-8-25 traslado a
planta.En planta de Neurocirugía la paciente permanece afebril, consciente, bradipsíquica. Emite lenguaje disártico pero coherente y
obedece ordenes sencillas. La zona de defecto craneal muestra buen aspecto y hundida por lo que se realiza TC Craneal de
control que ya no muestra datos de hipertensión intracraneal y se decide reposición ósea el día 5-8-25 sin incidencias. Durante el
postoperatorio la paciente presenta adecuada evolución clínica.
Tolera la sedestación y realiza tratamiento con fisioterapia y logopeda que permite iniciar tolerancia oral a dieta turmix.
La herida quirúrgica muestra buen aspecto con pequeño acúmulo subcutáneo pero sin exudado. Se procede a retirar sonda vesical
sin incidencias.
Valorada por Psiquiatría por labilidad emocional con llanto fácil quien ajusta el tratamiento.Dada las secuelas neurológicas y la necesidad de tratamiento rehabilitador se solicita traslado a centro de daño cerebral (Centro
Casaverde)Tras ser aceptada, es trasladada en el día de hoy

DIAGNOSTICO PRINCIPAL:Politraumatismo por precipitación:
- HSA izquierda. - Hematoma subdural izquierdo a nivel de tentorio y temporal.
- Focos contusivos temporales izquierdos con edema asociado.
- Fx temporal derecha que se extiende hasta peñasco con ocupación de celdillas mastorideas y oído medio.
- Neumoencéfalo en hemisferio cerebeloso derecho.
- Línea de fractura de huesos propios.
- Fx de apófisis pterigoidea izquierda y de la pared lateral de seno esfenoidal ipsilateral hasta vértice de la órbita.
- Fx del proceso clinoideo anterior izquierdo
- Fx del suelo-pared anterior del seno maxilar izquierdo con engrosamiento mucoperióstico.
- Neumotórax en región apical, anterior y basal derecho y derrame pleural derecho drenado con tubo de tórax
- Fractura  conminuta y desplazada del tercio medio-distal de la clavícula derecha. Tratamiento ortopédico- Fractura radio distal derecho. Reducción cerrada + inmovilización con yeso antebraquiopalmar.   - Fx de arcos costales
posterior derechos: tercero, cuarto (acabalgado), quinto,sexto, séptimo, octavo y noveno (acabalgado y desplazado) y décimo
(doble) y duodécimo (costilla rudimentaria).
- Línea de  fractura en tercio anterior de arco costales derechos: cuarto (dudosos), quinto (acabalgado), séptimo y octavo
(desplazado) y noveno
- Fx de apófisis transversa derecha de T7 y T8 y de L1 y L2 (estas dos últimas desplazadas).
- Fractura del cuerpo vertebral de L3.Craniectomía descompresiva. Reposición ósea
Traqueostomía cerrada
GastrostomíaDIAGNOSTICO SECUNDARIO:Trastorno cognoscitivo secundario a TCE.
Leve hemiparesia derecha
Disfasia del lenguaje`
    }
  ]);;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = applyModeDefaults({
      ...formData,
      locale: formData.locale || locale,
    });

    if (!normalized.text.trim()) {
      alert(t('form.enter_text_alert', 'Please enter some medical text to analyze'));
      return;
    }

    if (!normalized.run_diagnostic && !normalized.run_pcs) {
      setModeError(t('form.mode_required', 'Select at least one extraction mode.'));
      return;
    }

    setModeError(null);
    setFormData(normalized);

    if (isExtracting) {
      setPendingQueue(q => {
        if (q.length >= MAX_QUEUE_LENGTH) {
          setQueueLimitAlert(true);
          return q;
        }
        setQueueLimitAlert(false);
        return [...q, normalized];
      });
      setJustQueued(true);
      if (justQueuedTimerRef.current) window.clearTimeout(justQueuedTimerRef.current);
      justQueuedTimerRef.current = window.setTimeout(() => setJustQueued(false), 3000);
      return; // keep text for iterative refinement
    }
    setCurrentRequest(normalized);
    onSubmit(normalized);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({ ...formData, text: e.target.value });
  };

  const handleSampleSelect = (sampleText: string) => {
    setFormData({ ...formData, text: sampleText });
  };

  // Get provider icon component
  const getProviderIcon = (provider?: string) => {
    switch (provider?.toLowerCase()) {
      case 'openai':
        return <AiOutlineOpenAI className="text-[16px] text-muted-foreground" title="OpenAI" />;
      case 'anthropic':
        return <SiAnthropic className="text-[16px] text-muted-foreground" title="Anthropic" />;
      case 'google':
        return <SiGoogle className="text-[16px] text-muted-foreground" title="Google" />;
      case 'xai':
        return <BsTwitterX className="text-[16px] text-muted-foreground" title="xAI" />;
      case 'openrouter':
        return <OpenRouterIcon className="text-[16px] text-muted-foreground" title="OpenRouter" />;
      default:
        return null;
    }
  };

  // Format provider name for display
  const formatProviderName = (provider?: string) => {
    if (!provider) return '—';
    switch (provider.toLowerCase()) {
      case 'openai':
        return 'OpenAI';
      case 'anthropic':
        return 'Anthropic';
      case 'google':
        return 'Google';
      case 'xai':
        return 'xAI';
      case 'openrouter':
        return 'OpenRouter';
      default:
        return provider;
    }
  };

  return (
    <div className="w-full">
      <Card>
        <CardHeader>
          <CardTitle>{t('form.title', 'ICD-10 Code Extraction')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Medical Text Input */}
            <div className="space-y-2">
              <Label htmlFor="medicalText">{t('form.medical_text', 'Medical Text')}</Label>
              <Textarea
                id="medicalText"
                value={formData.text}
                onChange={handleTextChange}
                onInput={handleTextChange}
                placeholder={t('form.placeholder', 'Enter medical text, diagnosis, or clinical notes to extract ICD-10 codes...')}
                className="min-h-[150px]"
              />
            </div>

            {/* Extraction Modes */}
            <div className="space-y-2">
              <Label>{t('form.mode_label', 'Extraction Modes')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('form.mode_helper', 'Choose which agents to run for this request.')}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                <label className="inline-flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={formData.run_diagnostic !== false}
                    onCheckedChange={handleModeToggle('run_diagnostic')}
                  />
                  <span>{t('form.mode_diagnostic', 'ICD-10 Diagnosis')}</span>
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={!!formData.run_pcs}
                    onCheckedChange={handleModeToggle('run_pcs')}
                  />
                  <span>{t('form.mode_pcs', 'ICD-10-PCS Procedures')}</span>
                </label>
              </div>
              {modeError && (
                <p className="text-xs text-destructive">{modeError}</p>
              )}
            </div>

            {/* Agent Preset Selection */}
            <div className="space-y-2">
              <Label>{t('form.agent_preset_label', 'Agent Configuration')}</Label>
              <p className="text-xs text-muted-foreground">
                {t('form.agent_preset_helper', 'Choose the speed/accuracy trade-off for extraction.')}
              </p>
              {presetsLoading ? (
                <div className="text-sm text-muted-foreground">{t('form.loading_presets', 'Loading presets...')}</div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-stretch">
                  {Object.entries(agentPresets).map(([key, preset]) => {
                    const displayInfo = PRESET_DISPLAY_INFO[key];
                    if (!displayInfo) return null; // Skip presets without display metadata
                    
                    const providerKey = preset.primary_provider ?? preset.requires_providers?.[0];
                    const providerIcon = providerKey ? getProviderIcon(providerKey) : null;
                    const reasoningModel = preset.models?.complex_reasoning;
                    const tasksModel = preset.models?.simple_tasks;
                    const costTone =
                      preset.relative_cost === 'Low'
                        ? 'bg-green-500/10 text-green-400 border-green-500/20'
                        : preset.relative_cost === 'Medium'
                          ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                          : 'bg-orange-500/10 text-orange-400 border-orange-500/20';

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setFormData({ ...formData, agent_preset: key })}
                        disabled={!preset.available}
                        className={
                          `group relative p-4 rounded-xl border text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 h-full flex flex-col ` +
                          (formData.agent_preset === key
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : preset.available
                              ? 'border-border hover:border-primary/40 hover:bg-accent/60'
                              : 'border-border opacity-50 cursor-not-allowed')
                        }
                        aria-pressed={formData.agent_preset === key}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-base leading-none">{displayInfo.icon}</span>
                              <div className="font-semibold text-sm leading-tight line-clamp-2">
                                {t(displayInfo.nameKey, displayInfo.nameKey)}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {preset.relative_cost && (
                              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${costTone}`}>
                                {t(`form.cost_${preset.relative_cost.toLowerCase()}`, preset.relative_cost)}
                              </span>
                            )}
                          </div>
                        </div>

                        <p className="text-xs text-muted-foreground mt-3 leading-relaxed line-clamp-2">
                          {t(displayInfo.descriptionKey, displayInfo.descriptionKey)}
                        </p>

                        <div className="mt-auto pt-4">
                          <div className="pt-3 border-t border-border/50 space-y-2">
                            <div className="grid grid-cols-[72px_1fr] items-center gap-2 text-xs">
                              <span className="text-muted-foreground">{t('form.provider_label', 'Provider')}</span>
                              <span
                                className={`justify-self-end inline-flex items-center justify-center gap-1.5 font-light text-[14px] px-2 py-0.5 rounded-md bg-muted/40 border border-border/60 text-center truncate ${
                                  !providerKey ? 'text-muted-foreground' : ''
                                }`}
                                title={providerKey ? formatProviderName(providerKey) : undefined}
                              >
                                <span className="truncate">{formatProviderName(providerKey)}</span>
                                {providerIcon}
                              </span>
                            </div>

                            <div className="grid grid-cols-[72px_1fr] items-center gap-2 text-xs">
                              <span className="text-muted-foreground">{t('form.reasoning_label', 'Reasoning')}</span>
                              <span className="justify-self-end font-mono text-[11px] px-2 py-0.5 rounded-md bg-muted/40 border border-border/60 w-40 text-center truncate">
                                {reasoningModel ? reasoningModel : '—'}
                              </span>
                            </div>

                            <div className="grid grid-cols-[72px_1fr] items-center gap-2 text-xs">
                              <span className="text-muted-foreground">{t('form.tasks_label', 'Tasks')}</span>
                              <span className={`justify-self-end font-mono text-[11px] px-2 py-0.5 rounded-md bg-muted/40 border border-border/60 w-40 text-center truncate`}>
                                {tasksModel
                                  ? tasksModel
                                  : reasoningModel}
                              </span>
                            </div>
                          </div>
                        </div>

                        {!preset.available && preset.unavailable_reason && (
                          <div className="text-xs text-destructive mt-2">
                            {preset.unavailable_reason}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sample Texts */}
            <div className="space-y-2">
              <Label>{t('form.sample_texts_label', 'Sample Texts')}</Label>
              <div className="flex flex-wrap gap-2">
                {sampleTexts.map((sample, index) => (
                  <Button
                    key={index}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleSampleSelect(sample.text)}
                    className="flex flex-auto px-3"
                  >
                    {t(`form.sample_names.${sample.nameKey}`, sample.nameKey)}
                  </Button>
                ))}
              </div>
            </div>

            {/* User Feedback (optional) */}
            <div className="space-y-2">
              <Label htmlFor="feedback">{t('form.user_feedback', 'Feedback (optional)')}</Label>
              <Textarea
                id="feedback"
                value={formData.feedback || ''}
                onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                placeholder={t('form.feedback_placeholder', 'Any notes or feedback about this extraction')}
                className="min-h-[80px]"
              />
            </div>

            {/* Submit & Control Buttons */}
            <div className="flex flex-wrap gap-3 justify-end">
              {isExtracting && (
                <Button type="button" variant="outline" onClick={handleCancel} className="px-6">
                  {t('form.cancel', 'Cancel')}
                </Button>
              )}
              {lastCanceledRequest && !isExtracting && (
                <Button type="button" variant="outline" onClick={handleRerunCanceled} className="px-6">
                  {t('form.rerun_last', 'Rerun Last Canceled')}
                </Button>
              )}
              <Button
                type="submit"
                disabled={!isConnected || !formData.text.trim() || !hasSelectedMode}
                size="lg"
                className="px-8 py-3"
              >
                <HiOutlineSparkles /> {isExtracting ? t('form.queue_submit', 'Queue Extraction') : t('form.submit', 'Extract ICD-10 Codes')}
              </Button>
            </div>

            {/* Connection Warning */}
            {!isConnected && (
              <Alert>
                <MdError className='self-center' size={24} style={{ color: 'red' }} />
                <AlertTitle>{t('form.not_connected', 'Not connected to server.')}</AlertTitle>
                <AlertDescription>
                  {t('form.connection_required', 'Please connect to the server to enable extraction.')}
                </AlertDescription>
              </Alert>
            )}

            {/* Queue Limit Alert */}
            {queueLimitAlert && (
              <Alert>
                <AlertTitle>{t('form.queue_limit_reached', 'Queue limit reached')}</AlertTitle>
                <AlertDescription>{t('form.queue_limit_desc', `You can only queue up to ${MAX_QUEUE_LENGTH} items.`)}</AlertDescription>
              </Alert>
            )}

            {/* Queue Status */}
            {(pendingQueue.length > 0 || justQueued) && (
              <Alert>
                <HiOutlineSparkles className='self-center' size={24} />
                <AlertTitle>
                  {justQueued
                    ? t('form.queued_title', 'Added to queue')
                    : t('form.queue_status_title', 'Pending extractions')}
                </AlertTitle>
                <AlertDescription>
                  {t('form.queue_length', 'Items in queue:')} {pendingQueue.length}
                </AlertDescription>
              </Alert>
            )}

            {/* Queue List */}
            {pendingQueue.length > 0 && (
              <div className="space-y-2">
                <Label>{t('form.queue_list', 'Queued Requests')}</Label>
                <ul className="space-y-2">
                  {pendingQueue.map((item, idx) => (
                    <li key={idx} className="border rounded p-3 flex flex-col gap-2 bg-muted/20">
                      <div className="text-xs line-clamp-2 whitespace-pre-wrap">
                        {item.text.slice(0, 160)}{item.text.length > 160 ? '…' : ''}
                      </div>
                      <div className="flex flex-wrap gap-2 justify-end">
                        <Button type="button" variant="outline" size="sm" onClick={() => moveQueued(idx, -1)} disabled={idx === 0}>{t('form.up', 'Up')}</Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => moveQueued(idx, 1)} disabled={idx === pendingQueue.length - 1}>{t('form.down', 'Down')}</Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => editQueued(idx)}>{t('form.edit', 'Edit')}</Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => removeQueued(idx)}>{t('form.remove', 'Remove')}</Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

          </form>
        </CardContent>
      </Card>
    </div>
  );
};
